import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { User, TrendingUp, Map, Activity, Sparkles, Copy, Check, ExternalLink, ChevronUp, Plus } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useBrand } from '../../context/BrandContext';
import { useVipSource } from '../../context/VipSourceContext';
import IssueBonusForm from '../vip_private/IssueBonusForm';
import { Spinner } from '../ui/spinner';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import TrajectoryChart, { ZoomBar } from '../trajectory/TrajectoryChart';
import TrajectorySummaryCard from '../trajectory/TrajectorySummaryCard';
import BonusProfile from '../trajectory/BonusProfile';
import HeatmapGrid from '../heatmap/HeatmapGrid';
import PobaChart from '../churn/PobaChart';
import { fmtEur, fmtPct, fmtMin, fmtTime, METRIC_COLORS } from './playerFormat';
import { usePlayerContactLog } from '../vip/contact_tracker/usePlayerContactLog';
import { MetricsWindowBar, StatSection, SectionPill, RangeMetricCard, resolveMetricsRange, pctDelta, ppDelta, DEFAULT_CUSTOM_PRESET } from './MetricsWindow';

// ─── Formatters ───────────────────────────────────────────────────────────────

// Mirrors enrichPlayer() in DashboardView — builds the same shape from raw API row.
function enrichPlayerStats(p) {
  const n = (f) => parseFloat(p[f]) || 0;
  const turn7 = n('turnover_7d'), turn30 = n('turnover_30d');
  const bonus7 = n('bonus_7d'), bonus30 = n('bonus_30d');
  const bonusEur7 = n('bonus_eur_7d'), bonusEur30 = n('bonus_eur_30d');
  const ggr7 = n('ggr_7d'), ggr30 = n('ggr_30d');
  const dep7 = n('dep_7d'), dep30 = n('dep_30d');
  const cash7 = n('cash_7d'), cash30 = n('cash_30d');
  const ngr7 = n('ngr_7d'), ngr30 = n('ngr_30d');
  const bets7 = n('n_bets_7d'), bets30 = n('n_bets_30d');
  const play7 = n('playtime_7d_sec');
  const weeklyNgr = ['w1_ngr','w2_ngr','w3_ngr','w4_ngr','w5_ngr','w6_ngr']
    .map(k => p[k] == null ? null : parseFloat(p[k]));
  const avgWeekly = turn30 / 4.28;
  const recentMult = avgWeekly > 0 ? turn7 / avgWeekly : (turn7 > 0 ? 2 : 0);
  const trajectory = turn30 === 0 ? 'inactive'
    : recentMult > 1.2 ? 'up' : recentMult < 0.8 ? 'down' : 'flat';
  const parts = (p.username || p.email || '').split(/[\s@._-]/);
  const initials = ((parts[0]?.[0] || '') + (parts[1]?.[0] || parts[0]?.[1] || '')).toUpperCase();

  // Parse payment methods — API returns a JSON array string or null
  let payMethods = [];
  try { payMethods = JSON.parse(p.pay_systems || '[]'); } catch {}
  if (!Array.isArray(payMethods)) payMethods = [];

  return {
    ...p,
    dep_7d: dep7, dep_30d: dep30,
    cash_7d: cash7, cash_30d: cash30,
    ngr_7d: ngr7, ngr_30d: ngr30, life_ngr: n('life_ngr'),
    ggr_7d: ggr7, ggr_30d: ggr30,
    turnover_7d: turn7, turnover_30d: turn30,
    bonus_7d: bonus7, bonus_30d: bonus30,
    playtime_7d_sec: play7, playtime_30d_sec: n('playtime_30d_sec'),
    wl6: weeklyNgr.map(h => h === null ? null : h < 0),
    trajectory, recentMult,
    bonus_eur_7d: bonusEur7, bonus_eur_30d: bonusEur30,
    bonusEurDaily7:  bonusEur7  / 7,
    bonusEurDaily30: bonusEur30 / 30,
    ngrDaily7:  ngr7  / 7,
    ngrDaily30: ngr30 / 30,
    comp_signal: (() => {
      const bonusAccel = bonusEur7 / 7 - bonusEur30 / 30; // negative = accelerating (field is negative cost)
      const ngrChange  = ngr7 / 7 - ngr30 / 30;
      if (bonusAccel < 0 && ngrChange < 0)  return 'burning';
      if (bonusAccel < 0 && ngrChange >= 0) return 'efficient';
      if (ngrChange  < 0)                   return 'ngr_down';
      return 'ok';
    })(),
    bonusPct:        turn7  > 0 ? (bonus7 / turn7)   * 100 : 0,
    avgBet7d:        bets7  > 0 ? turn7   / bets7          : 0,
    avgBet30d:       bets30 > 0 ? turn30  / bets30         : 0,
    houseEdgePct7d:  turn7  > 0 ? (ggr7   / turn7)  * 100 : null,
    houseEdgePct30d: turn30 > 0 ? (ggr30  / turn30) * 100 : null,
    ngrDepRatio7d:   dep7   > 0 ? (ngr7   / dep7)   * 100 : null,
    ngrDepRatio30d:  dep30  > 0 ? (ngr30  / dep30)  * 100 : null,
    bonusGgrRatio7d: ggr7   > 0 ? (bonus7 / ggr7)   * 100 : null,
    bonusGgrRatio30d:ggr30  > 0 ? (bonus30/ ggr30)  * 100 : null,
    playtime: fmtTime(play7),
    payMethods,
    initials,
    poba:      p.prob_of_being_alive         != null ? parseFloat(p.prob_of_being_alive)         : null,
    poba7avg:  p.prob_of_being_alive_7d_avg  != null ? parseFloat(p.prob_of_being_alive_7d_avg)  : null,
    poba7min:  p.prob_of_being_alive_7d_min  != null ? parseFloat(p.prob_of_being_alive_7d_min)  : null,
    poba7max:  p.prob_of_being_alive_7d_max  != null ? parseFloat(p.prob_of_being_alive_7d_max)  : null,
    poba30avg: p.prob_of_being_alive_30d_avg != null ? parseFloat(p.prob_of_being_alive_30d_avg) : null,
    poba30min: p.prob_of_being_alive_30d_min != null ? parseFloat(p.prob_of_being_alive_30d_min) : null,
    poba30max: p.prob_of_being_alive_30d_max != null ? parseFloat(p.prob_of_being_alive_30d_max) : null,
    cltv:      p.cltv_label                  != null ? parseInt(p.cltv_label)                    : null,
    cltv7avg:  p.cltv_label_7d_avg           != null ? parseFloat(p.cltv_label_7d_avg)           : null,
    cltv30avg: p.cltv_label_30d_avg          != null ? parseFloat(p.cltv_label_30d_avg)          : null,
  };
}

const fmtDate = (raw) => {
  if (!raw) return null;
  const d = new Date(raw);
  if (isNaN(d)) return null;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtYearsAgo = (raw) => {
  if (!raw) return null;
  const d = new Date(raw);
  if (isNaN(d)) return null;
  const years = Math.floor((Date.now() - d) / (365.25 * 24 * 3600 * 1000));
  if (years < 1) return 'less than a year ago';
  return `${years}y ago`;
};

function playerInitials(name) {
  const parts = (name || '').split(/[\s@._-]/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || parts[0]?.[1] || '')).toUpperCase();
}

// ─── Shared primitives ────────────────────────────────────────────────────────

const TRAJ_COLORS = { up: '#00BD71', down: '#E21351', flat: '#7a8499', inactive: '#4a5568', neutral: '#5c6271' };
const TRAJ_RING   = { up: '#00BD71', down: '#E21351', flat: '#d4a843', inactive: '#6a7fa8', neutral: '#5c6271' };

const BADGE_STYLES = {
  warning:  { bg: 'rgba(255,210,47,0.15)',  color: '#FFD22F' },
  negative: { bg: 'rgba(226,19,81,0.15)',   color: '#E21351' },
  positive: { bg: 'rgba(0,189,113,0.15)',   color: '#00BD71' },
  muted:    { bg: 'rgba(154,160,173,0.15)', color: '#9aa0ad' },
  info:     { bg: 'rgba(79,195,247,0.15)',  color: '#4fc3f7' },
};

function getBadges(p, pobaDelta = 0.03) {
  if (!p) return [];
  const b = [];

  if (p.is_vip === false)
    b.push({ label: 'Not a VIP', variant: 'muted', tooltip: 'This player is not in the VIP programme.' });

  // PoBA health (current value)
  if (p.poba != null) {
    if (p.poba >= 0.7)
      b.push({ label: 'PoBA healthy',  variant: 'positive', tooltip: `PoBA ${(p.poba * 100).toFixed(0)}% — probability of being alive is high. Player is likely still active.` });
    else if (p.poba >= 0.4)
      b.push({ label: 'PoBA at risk',  variant: 'warning',  tooltip: `PoBA ${(p.poba * 100).toFixed(0)}% — early churn signals. Consider a targeted retention offer.` });
    else
      b.push({ label: 'PoBA alarming', variant: 'negative', tooltip: `PoBA ${(p.poba * 100).toFixed(0)}% — high churn risk. Immediate action recommended.` });
  }

  // PoBA trend: 7d min vs 30d avg
  if (p.poba7min != null && p.poba30avg != null && p.poba30avg - p.poba7min >= pobaDelta)
    b.push({ label: 'PoBA down', variant: 'negative', tooltip: `7d min PoBA (${(p.poba7min * 100).toFixed(0)}%) is ${((p.poba30avg - p.poba7min) * 100).toFixed(1)}pp below 30d avg (${(p.poba30avg * 100).toFixed(0)}%) — retention is deteriorating.` });
  else if (p.poba7avg != null && p.poba30avg != null && p.poba7avg - p.poba30avg >= pobaDelta)
    b.push({ label: 'PoBA up',   variant: 'positive', tooltip: `7d avg PoBA (${(p.poba7avg * 100).toFixed(0)}%) is ${((p.poba7avg - p.poba30avg) * 100).toFixed(1)}pp above 30d avg (${(p.poba30avg * 100).toFixed(0)}%) — retention is improving.` });

  if (p.comp_signal === 'burning')
    b.push({ label: 'Burning',   variant: 'negative', tooltip: 'Bonus spend accelerating while NGR is declining — bonuses are not converting to revenue.' });
  if (p.comp_signal === 'efficient')
    b.push({ label: 'Efficient', variant: 'positive', tooltip: 'Bonus spend up and NGR also improving — bonuses are converting to revenue.' });
  if (p.comp_signal === 'ngr_down')
    b.push({ label: 'NGR down',  variant: 'warning',  tooltip: 'NGR is declining despite stable bonus spend — player may be disengaging.' });

  return b;
}

function PlayerAvatar({ initials, trajectory }) {
  const fill = TRAJ_COLORS[trajectory] || TRAJ_COLORS.flat;
  const ring = TRAJ_RING[trajectory]   || TRAJ_RING.flat;
  return (
    <div
      className="rounded-full flex items-center justify-center shrink-0 font-bold"
      style={{ width: 52, height: 52, fontSize: 17, background: fill + '22', color: fill, border: `2px solid ${ring}88` }}
    >
      {initials}
    </div>
  );
}

function WLDots({ wl6 }) {
  return (
    <div className="flex gap-1.5 items-center">
      {[...(wl6 || [])].reverse().map((h, i, arr) => {
        const isNow = i === arr.length - 1;
        const color = h === null ? TRAJ_COLORS.neutral : h ? TRAJ_COLORS.up : TRAJ_COLORS.down;
        return (
          <Tooltip key={i}>
            <TooltipTrigger asChild>
              <div
                className="w-2 h-2 rounded-full shrink-0 cursor-default"
                style={{ background: color, outline: isNow ? `2px solid ${color}` : 'none', outlineOffset: '1px' }}
              />
            </TooltipTrigger>
            <TooltipContent>{isNow ? 'current week' : `${arr.length - 1 - i}w ago`}</TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

function BadgeChip({ label, variant, tooltip }) {
  const s = BADGE_STYLES[variant] || BADGE_STYLES.muted;
  const chip = (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-bold h-5 whitespace-nowrap"
      style={{ background: s.bg, color: s.color }}
    >
      {label}
    </span>
  );
  if (!tooltip) return chip;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{chip}</TooltipTrigger>
      <TooltipContent className="max-w-[220px] text-center">{tooltip}</TooltipContent>
    </Tooltip>
  );
}

// Returns { pct, avg7d, avg30d } comparing daily rates. avg values are raw (pre-format).
function dailyTrend(v7d, v30d) {
  if (v7d == null || v30d == null) return null;
  const avg7d = v7d / 7;
  const avg30d = v30d / 30;
  if (avg30d === 0 || !isFinite(avg30d)) return null;
  return { pct: ((avg7d - avg30d) / Math.abs(avg30d)) * 100, avg7d, avg30d };
}

// up = green, down = red always. Tooltip shows actual daily averages on hover.
function TrendBadge({ pct, avg7d, avg30d, fmt }) {
  if (pct == null || !isFinite(pct) || Math.abs(pct) < 3) return null;
  const up    = pct > 0;
  const color = up ? '#00c896' : '#e05555';
  const tip   = fmt ? `7d avg: ${fmt(avg7d)} · 30d avg: ${fmt(avg30d)}` : null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="block text-[9px] font-medium mt-0.5 cursor-default" style={{ color }}>
          {up ? '↑' : '↓'} {Math.abs(pct).toFixed(0)}% vs 30d avg
        </span>
      </TooltipTrigger>
      {tip && <TooltipContent>{tip}</TooltipContent>}
    </Tooltip>
  );
}

function StatCard({ label, value, color, trend }) {
  return (
    <div className="rounded-lg border border-white/[0.07] bg-white/[0.03] px-4 py-3">
      <p className="text-[9px] text-ink-dim uppercase tracking-wide mb-0.5">{label}</p>
      <p className="text-sm font-bold text-ink" style={color ? { color } : {}}>
        {value}
      </p>
      {trend && <TrendBadge {...trend} />}
    </div>
  );
}

function DualStatCard({ label, value7, color7, trend7, value30, color30 }) {
  return (
    <div className="rounded-lg border border-white/[0.07] bg-white/[0.03] px-4 py-3 flex gap-3">
      <div className="flex-1 min-w-0">
        <p className="text-[9px] text-ink-dim uppercase tracking-wide mb-0.5">7d {label}</p>
        <p className="text-sm font-bold text-ink" style={color7 ? { color: color7 } : {}}>{value7}</p>
        {trend7 && <TrendBadge {...trend7} />}
      </div>
      <div className="w-px bg-white/10 self-stretch shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[9px] text-ink-dim uppercase tracking-wide mb-0.5">30d {label}</p>
        <p className="text-sm font-bold text-ink" style={color30 ? { color: color30 } : {}}>{value30}</p>
      </div>
    </div>
  );
}

const COMP_SIGNAL_META = {
  burning:   { label: 'Burning',   variant: 'negative', desc: 'Bonus spend up and NGR declining — bonuses are not paying off.' },
  efficient: { label: 'Efficient', variant: 'positive', desc: 'Bonus spend up but NGR is also improving — bonuses are converting.' },
  ngr_down:  { label: 'NGR down',  variant: 'warning',  desc: 'NGR is declining despite stable bonus spend.' },
  ok:        { label: 'OK',        variant: 'positive', desc: 'Bonus spend stable and NGR holding — no intervention needed.' },
};

function CompRateRow({ label, daily7, daily30, positiveIsGood }) {
  const diff = (daily7 ?? 0) - (daily30 ?? 0);
  const isUp = diff > 0;
  const isGood = positiveIsGood ? isUp : !isUp;
  const arrowColor = Math.abs(diff) < 0.5 ? '#7a8499' : (isGood ? '#00BD71' : '#E21351');
  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-[9px] text-ink-dim uppercase tracking-wide">{label}</p>
      <div className="flex items-baseline gap-1">
        <span className="text-[9px] text-ink-dim font-normal mr-0.5">7d</span>
        <span className="text-sm font-bold text-ink">{fmtEur(daily7)}<span className="text-[9px] text-ink-dim font-normal">/d</span></span>
        <span className="text-[10px]" style={{ color: arrowColor }}>{isUp ? '↑' : '↓'}</span>
      </div>
      <span className="text-[10px] text-ink-dim">30d {fmtEur(daily30)}/d</span>
    </div>
  );
}

function CompSignalPanel({ player }) {
  if (!player) return null;
  const meta = COMP_SIGNAL_META[player.comp_signal] || COMP_SIGNAL_META.ok;
  const BADGE_STYLES_LOCAL = {
    warning:  { bg: 'rgba(255,210,47,0.15)',  color: '#FFD22F' },
    negative: { bg: 'rgba(226,19,81,0.15)',   color: '#E21351' },
    positive: { bg: 'rgba(0,189,113,0.15)',   color: '#00BD71' },
  };
  const s = BADGE_STYLES_LOCAL[meta.variant];
  return (
    <div className="rounded-lg border border-white/10 bg-surface-1 px-4 py-3 flex flex-col gap-3">
      <p className="text-[9px] text-ink-dim uppercase tracking-widest">Comp Signal</p>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-bold h-5 whitespace-nowrap" style={{ background: s.bg, color: s.color }}>
          {meta.label}
        </span>
        <span className="text-[11px] text-ink-dim">{meta.desc}</span>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <CompRateRow label="Bonus cost/day" daily7={-player.bonusEurDaily7} daily30={-player.bonusEurDaily30} positiveIsGood={false} />
        <CompRateRow label="NGR/day" daily7={player.ngrDaily7} daily30={player.ngrDaily30} positiveIsGood={true} />
      </div>
    </div>
  );
}

// Custom-mode Performance/Money/Ratios — single range-scoped metric per card, no
// Lifetime NGR card, delta vs the immediately preceding period of equal length.
function RangeStatSections({ metricsWindow }) {
  const { resolved, data, loading, compare } = metricsWindow;
  const cur = data?.current || {};
  const prev = compare ? data?.previous : null;
  const showLoading = loading;

  return (
    <>
      <StatSection label="Performance" pill={<SectionPill mode="custom" resolved={resolved} />}>
        <RangeMetricCard
          label="NGR"
          value={fmtEur(cur.ngr)}
          color={cur.ngr >= 0 ? METRIC_COLORS.positive : METRIC_COLORS.negative}
          delta={prev && pctDelta(cur.ngr, prev.ngr)}
          loading={showLoading}
        />
        <RangeMetricCard
          label="GGR"
          value={fmtEur(cur.ggr)}
          color={cur.ggr >= 0 ? METRIC_COLORS.positive : METRIC_COLORS.negative}
          delta={prev && pctDelta(cur.ggr, prev.ggr)}
          loading={showLoading}
        />
        <RangeMetricCard
          label="Turnover"
          value={fmtEur(cur.turnover)}
          delta={prev && pctDelta(cur.turnover, prev.turnover)}
          loading={showLoading}
        />
      </StatSection>

      <StatSection label="Money" pill={<SectionPill mode="custom" resolved={resolved} />}>
        <RangeMetricCard
          label="Deposits"
          value={fmtEur(cur.deposits)}
          delta={prev && pctDelta(cur.deposits, prev.deposits)}
          loading={showLoading}
        />
        <RangeMetricCard
          label="Cashouts"
          value={fmtEur(cur.cashouts)}
          delta={prev && pctDelta(cur.cashouts, prev.cashouts)}
          loading={showLoading}
        />
        <RangeMetricCard
          label="Bonus"
          value={fmtEur(cur.bonus)}
          color={cur.bonus > 0 ? METRIC_COLORS.warning : undefined}
          delta={prev && pctDelta(cur.bonus, prev.bonus)}
          loading={showLoading}
        />
        <RangeMetricCard
          label="Playtime"
          value={cur.playtime_sec != null ? fmtMin(cur.playtime_sec / 60) : '—'}
          delta={prev && pctDelta(cur.playtime_sec, prev.playtime_sec)}
          loading={showLoading}
        />
      </StatSection>

      <StatSection label="Ratios" pill={<SectionPill mode="custom" resolved={resolved} />}>
        <RangeMetricCard
          label="Avg Bet"
          value={cur.avg_bet > 0 ? `${cur.avg_bet.toFixed(1)}€` : '—'}
          delta={prev && pctDelta(cur.avg_bet, prev.avg_bet)}
          loading={showLoading}
        />
        <RangeMetricCard
          label="House Edge"
          value={fmtPct(cur.house_edge_pct)}
          color={cur.house_edge_pct != null && cur.house_edge_pct > 0 ? METRIC_COLORS.positive : undefined}
          delta={prev && ppDelta(cur.house_edge_pct, prev.house_edge_pct)}
          loading={showLoading}
        />
        <RangeMetricCard
          label="NGR/Dep"
          value={fmtPct(cur.ngr_dep_ratio)}
          color={cur.ngr_dep_ratio != null ? (cur.ngr_dep_ratio > 0 ? METRIC_COLORS.positive : METRIC_COLORS.negative) : undefined}
          delta={prev && ppDelta(cur.ngr_dep_ratio, prev.ngr_dep_ratio)}
          loading={showLoading}
        />
        <RangeMetricCard
          label="Bonus/GGR"
          value={fmtPct(cur.bonus_ggr_ratio)}
          color={cur.bonus_ggr_ratio != null && cur.bonus_ggr_ratio > 100 ? METRIC_COLORS.negative : undefined}
          delta={prev && ppDelta(cur.bonus_ggr_ratio, prev.bonus_ggr_ratio)}
          loading={showLoading}
        />
      </StatSection>
    </>
  );
}

// ─── Player Games section ─────────────────────────────────────────────────────

const BACKOFFICE_BET_URL = {
  bitstarz: 'https://backoffice.bitstarz-kute.com/backend/bets',
  cashy:    'https://backoffice.cashy-kaga.com/backend/bets',
};

const BACKOFFICE_PLAYER_URL = {
  bitstarz: 'https://backoffice.bitstarz-kute.com/backend/players',
  cashy:    'https://backoffice.cashy-kaga.com/backend/players',
};

function fmtMult(m) {
  if (!m || m <= 0) return null;
  return m >= 1000 ? `${(m / 1000).toFixed(1)}K×` : `${m.toFixed(0)}×`;
}

function BetLink({ betId, backofficeBase }) {
  if (!betId || !backofficeBase) return null;
  return (
    <a
      href={`${backofficeBase}/${betId}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="font-mono text-[10px] text-accent/70 hover:text-accent transition-colors"
      title={`Open bet #${betId} in backoffice`}
    >
      #{betId}
    </a>
  );
}

// desc-only sorts never toggle direction; ggr is the only bidirectional one
const GAME_COLS = [
  { key: 'turnover',      label: 'Turnover', get: g => g.turnover,           bidir: false, w: 'w-16', sep: false },
  { key: 'ggr',          label: 'GGR',      get: g => g.ggr,                bidir: true,  w: 'w-14', sep: true  },
  { key: 'n_bets',       label: 'Bets',     get: g => g.n_bets,             bidir: false, w: 'w-11', sep: true  },
  { key: 'biggest_win',  label: 'Top win',  get: g => g.biggest_win_payoff, bidir: false, w: 'w-14', sep: true  },
  { key: 'biggest_mult', label: 'Top ×',    get: g => g.biggest_multiplier, bidir: false, w: 'w-10', sep: true  },
];

function GameCard({ game: g, index, backofficeBase }) {
  const ggrColor = g.ggr >= 0 ? '#00BD71' : '#E21351';
  const hasBigWin  = g.biggest_win_payoff > 0;
  const hasBigMult = g.biggest_multiplier > 0;
  const fmtPayoff = (v) => fmtEur(v);
  // Alternate every 5 cards (one visual row)
  const alt = Math.floor(index / 5) % 2 === 1;
  const cardBg  = alt ? 'bg-[#161920]' : 'bg-surface-1';
  const cellBg  = alt ? 'bg-[#161920]' : 'bg-surface-1';

  return (
    <div className={`rounded-lg border border-white/10 ${cardBg} hover:brightness-110 transition-all flex flex-col`}>
      {/* Title */}
      <div className="p-2.5 pb-2 flex items-start gap-1.5">
        <span className="text-[9px] text-ink-dim/40 tabular-nums font-mono mt-0.5 w-3 shrink-0">{index + 1}</span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold text-ink leading-snug">{g.game_title || '—'}</p>
          <p className="text-[9px] text-ink-dim/60 mt-0.5">{g.provider || ''}</p>
        </div>
      </div>

      {/* Core stats — always 3 fixed columns */}
      <div className="grid grid-cols-3 gap-px bg-white/[0.04] border-t border-white/[0.04]">
        {[
          { label: 'Turnover', value: fmtEur(g.turnover),       color: '#f4f1ec' },
          { label: 'GGR',      value: fmtEur(g.ggr),            color: ggrColor  },
          { label: 'Bets',     value: g.n_bets.toLocaleString(), color: '#9aa0ad' },
        ].map(({ label, value, color }) => (
          <div key={label} className={`${cellBg} px-2 py-1.5`}>
            <p className="text-[8px] text-ink-dim uppercase tracking-wide mb-0.5">{label}</p>
            <p className="text-[11px] font-bold" style={{ color }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Win / mult — always rendered for consistent card height */}
      <div className="border-t border-white/[0.04] grid grid-cols-2 gap-px bg-white/[0.04]">
        <div className={`${cellBg} px-2 py-1.5`}>
          {hasBigWin ? (
            <>
              <p className="text-[8px] text-ink-dim uppercase tracking-wide mb-0.5">Top win</p>
              <p className="text-[11px] font-bold text-[#00BD71]">{fmtPayoff(g.biggest_win_payoff)}</p>
              <p className="text-[9px] text-ink-dim/60">
                {fmtPayoff(g.biggest_win_stake)} bet
                {g.biggest_win_multiplier > 0 && <span className="text-amber-400/80"> · {fmtMult(g.biggest_win_multiplier)}</span>}
              </p>
              <BetLink betId={g.bet_id_win_max} backofficeBase={backofficeBase} />
            </>
          ) : <span className="text-ink-dim/20 text-[10px]">—</span>}
        </div>
        <div className={`${cellBg} px-2 py-1.5`}>
          {hasBigMult ? (
            <>
              <p className="text-[8px] text-ink-dim uppercase tracking-wide mb-0.5">Top ×</p>
              <p className="text-[11px] font-bold text-amber-400">{fmtMult(g.biggest_multiplier)}</p>
              <p className="text-[9px] text-ink-dim/60">
                {fmtPayoff(g.biggest_mult_stake)} bet
                {g.biggest_mult_payoff > 0 && <span className="text-[#00BD71]/80"> · {fmtPayoff(g.biggest_mult_payoff)}</span>}
              </p>
              <BetLink betId={g.bet_id_multiplier_max} backofficeBase={backofficeBase} />
            </>
          ) : <span className="text-ink-dim/20 text-[10px]">—</span>}
        </div>
      </div>
    </div>
  );
}

function PlayerGamesSection({ games, days, setDays, loading, activeBrand }) {
  const backofficeBase = BACKOFFICE_BET_URL[activeBrand] || BACKOFFICE_BET_URL.bitstarz;
  const [sortKey, setSortKey] = useState('turnover');
  const [sortDir, setSortDir] = useState('desc');

  const handleSort = (key) => {
    const col = GAME_COLS.find(c => c.key === key);
    if (key === sortKey && col?.bidir) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  if (loading && (!games || games.length === 0)) {
    return (
      <div className="rounded-lg border border-white/10 bg-surface-1 p-4">
        <p className="text-[9px] text-ink-dim uppercase tracking-widest mb-3">Top Games</p>
        <div className="flex justify-center py-4"><Spinner size="sm" className="text-ink-dim" /></div>
      </div>
    );
  }
  // No games loaded at all: nothing to show. An empty period still renders, so the 7d/30d toggle
  // stays reachable for a player who played only in the longer window.
  if (!games) return null;

  const sortCol = GAME_COLS.find(c => c.key === sortKey) || GAME_COLS[0];
  const sorted = [...games].sort((a, b) => {
    const av = sortCol.get(a) ?? 0;
    const bv = sortCol.get(b) ?? 0;
    return sortDir === 'desc' ? bv - av : av - bv;
  });

  return (
    <div className={`rounded-lg border border-white/10 bg-surface-1 overflow-hidden transition-opacity duration-150 ${loading ? 'opacity-60' : ''}`}>
      {/* Header: sort pills + period toggle */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/8 gap-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          <p className="text-[9px] text-ink-dim uppercase tracking-widest mr-1">Sort</p>
          {GAME_COLS.map(col => {
            const active = sortKey === col.key;
            return (
              <button
                key={col.key}
                onClick={() => handleSort(col.key)}
                className={`text-[10px] px-2.5 py-0.5 rounded-full border transition-colors inline-flex items-center gap-0.5 ${active ? 'border-cta text-cta bg-cta/10' : 'border-white/10 text-ink-dim hover:border-white/20 hover:text-ink'}`}
              >
                {col.label}
                {active && col.bidir && <span className="text-[8px]">{sortDir === 'desc' ? '↓' : '↑'}</span>}
                {active && !col.bidir && <span className="text-[8px]">↓</span>}
              </button>
            );
          })}
        </div>
        <div className="flex gap-1 shrink-0">
          {[7, 30].map(d => (
            <button key={d} onClick={() => setDays(d)}
              className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${days === d ? 'border-white/30 text-ink bg-white/8' : 'border-white/10 text-ink-dim hover:text-ink'}`}
            >{d}d</button>
          ))}
        </div>
      </div>

      {/* Card grid */}
      {sorted.length === 0 ? (
        <p className="px-4 py-6 text-xs text-ink-dim text-center">No games played in the last {days} days.</p>
      ) : (
        <div className="p-3 grid grid-cols-5 gap-2">
          {sorted.slice(0, 15).map((g, i) => (
            <GameCard
              key={g.game_title + g.provider}
              game={g}
              index={i}
              backofficeBase={backofficeBase}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Prediction helpers ───────────────────────────────────────────────────────

const CLTV_COLORS = { 0: '#7a8499', 1: '#E21351', 2: '#f0a500', 3: '#d4a843', 4: '#00BD71' };
const CLTV_LABELS = { 0: '≤ €10', 1: '€10–50', 2: '€50–200', 3: '€200–700', 4: '> €700' };

function pobaColor(poba) {
  if (poba < 0.3) return METRIC_COLORS.negative;
  if (poba < 0.5) return METRIC_COLORS.warning;
  return METRIC_COLORS.positive;
}

function pobaLabel(poba) {
  if (poba < 0.3) return 'High risk';
  if (poba < 0.5) return 'Elevated';
  if (poba < 0.7) return 'Moderate';
  return 'Low risk';
}

function StatsTable({ rows }) {
  // rows: [{ label, min, avg, max, fmt }]
  const showMin = rows.some(r => r.min != null);
  const showMax = rows.some(r => r.max != null);
  return (
    <table className="text-[10px] border-collapse w-full max-w-xs">
      <thead>
        <tr className="text-ink-dim">
          <th className="text-left pr-3 pb-1 font-medium"></th>
          {showMin && <th className="text-right pr-3 pb-1 font-medium">min</th>}
          <th className="text-right pr-3 pb-1 font-medium">avg</th>
          {showMax && <th className="text-right pr-3 pb-1 font-medium">max</th>}
          {showMin && showMax && <th className="text-right pb-1 font-medium">range</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.label} className="border-t border-white/5">
            <td className="text-ink-dim pr-3 py-1">{r.label}</td>
            {showMin && <td className="text-right pr-3 py-1 text-ink">{r.min != null ? r.fmt(r.min) : '—'}</td>}
            <td className="text-right pr-3 py-1 text-ink font-medium">{r.avg != null ? r.fmt(r.avg) : '—'}</td>
            {showMax && <td className="text-right pr-3 py-1 text-ink">{r.max != null ? r.fmt(r.max) : '—'}</td>}
            {showMin && showMax && (
              <td className="text-right py-1 text-ink-dim">
                {r.min != null && r.max != null ? r.fmt(r.max - r.min) : '—'}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Averages/min/max of poba and cltv_label over [fromStr, toStr] (inclusive, "YYYY-MM-DD"
// strings compare lexicographically) out of the player's full poba/cltv daily history.
function computeRangeMlStats(history, fromStr, toStr) {
  const rows = (history || []).filter((r) => {
    const d = (r.day || '').slice(0, 10);
    return d >= fromStr && d <= toStr;
  });
  const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);
  const pobaVals = rows.filter((r) => r.poba != null).map((r) => r.poba);
  const cltvVals = rows.filter((r) => r.cltv_label != null).map((r) => r.cltv_label);
  return {
    pobaMin: pobaVals.length ? Math.min(...pobaVals) : null,
    pobaAvg: avg(pobaVals),
    pobaMax: pobaVals.length ? Math.max(...pobaVals) : null,
    cltvAvg: avg(cltvVals),
  };
}

function PredictionsSection({ player, history, metricsWindow }) {
  const poba     = player.poba     != null ? player.poba     : null;
  const poba7avg = player.poba7avg != null ? player.poba7avg : null;
  const poba7min = player.poba7min != null ? player.poba7min : null;
  const poba7max = player.poba7max != null ? player.poba7max : null;
  const poba30avg = player.poba30avg != null ? player.poba30avg : null;
  const poba30min = player.poba30min != null ? player.poba30min : null;
  const poba30max = player.poba30max != null ? player.poba30max : null;
  const cltv      = player.cltv     != null ? player.cltv     : null;
  const cltv7avg  = player.cltv7avg  != null ? player.cltv7avg  : null;
  const cltv30avg = player.cltv30avg != null ? player.cltv30avg : null;

  if (poba === null && cltv === null) return null;

  const isCustom = metricsWindow?.mode === 'custom';
  const resolved = metricsWindow?.resolved;
  const rangeStats = isCustom && resolved ? computeRangeMlStats(history, resolved.fromStr, resolved.toStr) : null;

  const trendBase  = isCustom ? rangeStats?.pobaAvg : poba30avg;
  const trendLabel = isCustom ? `vs ${resolved?.days}d avg` : 'vs 30d avg';
  const pobaTrend  = poba !== null && trendBase != null ? poba - trendBase : null;

  const fmtPoba = v => `${(v * 100).toFixed(0)}%`;
  const fmtCltv = v => v.toFixed(1);

  const pobaRows = isCustom
    ? [{ label: `${resolved?.days}d`, min: rangeStats?.pobaMin, avg: rangeStats?.pobaAvg, max: rangeStats?.pobaMax, fmt: fmtPoba }]
    : [
        { label: '7d',  min: poba7min,  avg: poba7avg,  max: poba7max,  fmt: fmtPoba },
        { label: '30d', min: poba30min, avg: poba30avg, max: poba30max, fmt: fmtPoba },
      ];
  const cltvRows = isCustom
    ? [{ label: `${resolved?.days}d`, avg: rangeStats?.cltvAvg, fmt: fmtCltv }]
    : [
        { label: '7d',  avg: cltv7avg,  fmt: fmtCltv },
        { label: '30d', avg: cltv30avg, fmt: fmtCltv },
      ];

  return (
    <div className="rounded-lg border border-white/10 bg-surface-1 p-4">
      <div className="flex items-center gap-2 mb-4">
        <p className="text-[10px] text-ink-dim uppercase tracking-widest">ML Predictions</p>
        <SectionPill mode={isCustom ? 'custom' : 'standard'} resolved={resolved} />
      </div>
      <div className="flex flex-wrap gap-6">

        {/* PoBA */}
        {poba !== null && (
          <div className="flex flex-col gap-2 min-w-[180px]">
            <p className="text-[9px] text-ink-dim uppercase tracking-wide">Probability of Being Alive</p>
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-bold" style={{ color: pobaColor(poba) }}>{fmtPoba(poba)}</span>
              {pobaTrend !== null && (
                <span className="text-[10px] font-bold" style={{ color: pobaTrend > 0 ? METRIC_COLORS.positive : METRIC_COLORS.negative }}>
                  {pobaTrend > 0 ? '↑' : '↓'} {Math.abs(pobaTrend * 100).toFixed(1)}pp {trendLabel}
                </span>
              )}
            </div>
            <p className="text-[10px]" style={{ color: pobaColor(poba) }}>{pobaLabel(poba)}</p>
            <StatsTable rows={pobaRows} />
          </div>
        )}

        {/* Divider */}
        {poba !== null && cltv !== null && (
          <div className="w-px bg-white/10 self-stretch" />
        )}

        {/* CLTV */}
        {cltv !== null && (
          <div className="flex flex-col gap-2 min-w-[140px]">
            <p className="text-[9px] text-ink-dim uppercase tracking-wide">CLTV Bucket</p>
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-bold" style={{ color: CLTV_COLORS[cltv] || '#7a8499' }}>L{cltv}</span>
            </div>
            <p className="text-[10px] font-bold" style={{ color: CLTV_COLORS[cltv] || '#7a8499' }}>{CLTV_LABELS[cltv] || '?'}</p>
            <StatsTable rows={cltvRows} />
          </div>
        )}

      </div>
    </div>
  );
}

// ─── Peak-time widget ─────────────────────────────────────────────────────────

const DOW_SHORT = ['Su','Mo','Tu','We','Th','Fr','Sa'];

function PeakTimeCard({ cells }) {
  const [metric, setMetric] = useState('bets');

  const { dowT, hodT, grand } = useMemo(() => {
    const d = Array(7).fill(0), h = Array(24).fill(0);
    let g = 0;
    (cells || []).forEach(c => {
      const v = metric === 'bets' ? c.bet_count : (c.stake_eur || 0);
      d[c.dow] += v;
      h[c.hod] += v;
      g += v;
    });
    return { dowT: d, hodT: h, grand: g };
  }, [cells, metric]);

  if (!cells || cells.length === 0 || grand === 0) return null;

  const maxDow = Math.max(...dowT);
  const maxHod = Math.max(...hodT);

  const topDays = DOW_SHORT.filter((_, i) => dowT[i] >= maxDow * 0.75).join(', ');
  const peakHours = hodT
    .map((v, i) => ({ h: i, v }))
    .filter(x => x.v >= maxHod * 0.75)
    .map(x => `${String(x.h).padStart(2,'0')}h`);
  const peakStr = peakHours.length
    ? `${peakHours[0]}–${peakHours[peakHours.length - 1]} UTC`
    : null;

  const fmtTip = (v) => metric === 'bets'
    ? `${v.toLocaleString()} bets`
    : fmtEur(v);

  return (
    <div className="rounded-lg border border-white/10 bg-surface-1 px-4 py-3">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[9px] text-ink-dim uppercase tracking-wide">Most active time</p>
        <div className="flex gap-1">
          {['bets', 'money'].map(m => (
            <button
              key={m}
              onClick={() => setMetric(m)}
              className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${metric === m ? 'border-white/30 text-ink bg-white/8' : 'border-white/10 text-ink-dim hover:text-ink'}`}
            >
              {m === 'bets' ? 'Bets' : '€'}
            </button>
          ))}
        </div>
      </div>

      <p className="text-[11px] text-ink mb-3">
        {topDays}{peakStr ? ` · ${peakStr}` : ''}
      </p>

      {/* Day-of-week mini bars */}
      <div className="flex gap-1 mb-3">
        {dowT.map((v, i) => {
          const pct = maxDow > 0 ? v / maxDow : 0;
          const active = pct >= 0.75;
          return (
            <Tooltip key={i}>
              <TooltipTrigger asChild>
                <div className="flex flex-col items-center gap-0.5 flex-1 cursor-default">
                  <div className="w-full rounded-sm" style={{
                    height: 22,
                    background: active ? `rgba(212,168,67,${0.3 + pct * 0.65})` : `rgba(255,255,255,${0.04 + pct * 0.08})`,
                  }} />
                  <span className="text-[8px]" style={{ color: active ? '#d4a843' : '#4a5568' }}>
                    {DOW_SHORT[i]}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {DOW_SHORT[i]}: {grand > 0 ? ((v / grand) * 100).toFixed(1) : 0}% · {fmtTip(v)}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>

      {/* Hour-of-day mini bars */}
      <div className="flex gap-px">
        {hodT.map((v, i) => {
          const pct = maxHod > 0 ? v / maxHod : 0;
          const active = pct >= 0.75;
          return (
            <Tooltip key={i}>
              <TooltipTrigger asChild>
                <div className="flex-1 rounded-sm cursor-default" style={{
                  height: 12,
                  background: active ? `rgba(212,168,67,${0.3 + pct * 0.65})` : `rgba(255,255,255,${0.04 + pct * 0.1})`,
                }} />
              </TooltipTrigger>
              <TooltipContent>{String(i).padStart(2,'0')}h UTC: {grand > 0 ? ((v / grand) * 100).toFixed(1) : 0}% · {fmtTip(v)}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
      <div className="flex justify-between text-[8px] text-ink-dim mt-1">
        <span>0h</span><span>6h</span><span>12h</span><span>18h</span><span>23h UTC</span>
      </div>
    </div>
  );
}

// ─── Entropy helpers (heatmap tab) ────────────────────────────────────────────

const ENTROPY_COLORS = { low: '#d4a843', mid: '#9aa0ad', high: '#00BD71' };

function entropyColor(e) {
  if (e == null) return ENTROPY_COLORS.mid;
  if (e < 2) return ENTROPY_COLORS.low;
  if (e < 3) return ENTROPY_COLORS.mid;
  return ENTROPY_COLORS.high;
}

function StatTile({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-white/10 bg-surface-1 px-4 py-3">
      <p className="text-[11px] text-ink-dim mb-1">{label}</p>
      <p className="text-base font-bold text-ink">{value}</p>
      {sub && <p className="text-[10px] text-ink-dim mt-0.5">{sub}</p>}
    </div>
  );
}

// ─── Tab button ───────────────────────────────────────────────────────────────

function TabButton({ active, onClick, icon: Icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold border-b-2 -mb-px transition-colors ${
        active
          ? 'text-ink border-cta'
          : 'text-ink-dim border-transparent hover:text-ink'
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}

// ─── Datasource selector removed — all 4 datasources (heatmap, churn, bonuses, trajectory)
// are always included server-side. ──────────────────────────────────────────────────────

// ─── Tab content: Overview ────────────────────────────────────────────────────

function OverviewTabContent({ player, uid, initials, resolvedTrajectory, pobaHistory, heatmapCells, games, gamesDays, setGamesDays, gamesLoading, metricsWindow, contactAction, contactPanel, identityPanel, hidden }) {
  const { activeBrand } = useBrand();
  const [insightCards, setInsightCards] = useState([]);
  const [insightStreaming, setInsightStreaming] = useState(false);
  const [insightStatus, setInsightStatus] = useState('');
  const [insightError, setInsightError] = useState('');
  const insightAbortRef = useRef(null);
  useEffect(() => () => insightAbortRef.current?.abort(), []);

  const generateInsights = async () => {
    if (insightStreaming) { insightAbortRef.current?.abort(); return; }
    setInsightCards([]);
    setInsightError('');
    setInsightStatus('Generating…');
    setInsightStreaming(true);
    const controller = new AbortController();
    insightAbortRef.current = controller;

    const playerPayload = {
      ...(player || {}),
      weeklyHold: ['w1_ngr','w2_ngr','w3_ngr','w4_ngr','w5_ngr','w6_ngr']
        .map(k => player?.[k] != null ? parseFloat(player[k]) : null),
    };
    const headers = { 'Content-Type': 'application/json' };
    if (activeBrand) headers['X-Brand'] = activeBrand;

    try {
      const resp = await fetch('/api/vip/generate-insights', {
        method: 'POST',
        headers,
        body: JSON.stringify({ user_id: uid, player: playerPayload }),
        signal: controller.signal,
        credentials: 'include',
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        const msg = body.error || `HTTP ${resp.status}`;
        setInsightError(
          msg === 'insight_prompt_not_configured' ? 'LLM prompt not configured (Settings → Insights).' :
          msg === 'llm_key_not_configured'        ? 'LLM API key not set (Settings → Insights).' : msg
        );
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let lineBuffer = '', accumulated = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        lineBuffer += decoder.decode(value, { stream: true });
        const lines = lineBuffer.split('\n');
        lineBuffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') continue;
          try {
            const obj = JSON.parse(data);
            if (obj.text) {
              accumulated += obj.text;
              const parsed = parseInsightCards(accumulated);
              if (parsed.length > 0) { setInsightCards(parsed); setInsightStatus(''); }
            } else if (obj.status === 'tool_start') { setInsightStatus('Fetching player data…'); }
            else if (obj.status === 'tool_done')    { setInsightStatus('Analysing…'); }
            else if (obj.error) { setInsightError(obj.error); }
          } catch {}
        }
      }
      const finalCards = parseInsightCards(accumulated);
      if (finalCards.length > 0) {
        const hasData = finalCards.some(c => c.trigger || c.insight);
        if (!hasData) {
          setInsightError('Cards empty. Full raw:\n' + accumulated);
        } else {
          setInsightCards(finalCards);
        }
      } else {
        setInsightError('No cards found. Full raw:\n' + accumulated);
      }
    } catch (e) {
      setInsightError(e.name === 'AbortError' ? 'Cancelled.' : 'Stream error: ' + e.message);
    } finally {
      setInsightStreaming(false);
      setInsightStatus('');
    }
  };

  if (!player) {
    return (
      <div className="flex justify-center items-center min-h-[280px] rounded-lg border border-white/10 bg-surface-1 p-6">
        <Spinner size="sm" className="text-ink-dim" />
      </div>
    );
  }

  // A source without comp signals shows no comp badges.
  const badges = getBadges(hidden.comps ? { ...player, comp_signal: null } : player);

  return (
    <div className="flex flex-col gap-4">
      {/* Identity card */}
      <div className={cn('rounded-lg border bg-surface-1 p-5', identityPanel ? 'border-cta/40' : 'border-white/10')}>
        <div className="flex items-center gap-4 mb-4">
          <PlayerAvatar initials={initials} trajectory={resolvedTrajectory} />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <p className="text-sm font-bold text-ink">{player.username || player.email}</p>
              {badges.map((b, i) => <BadgeChip key={i} {...b} />)}
            </div>
            {player.email && player.email !== player.username && (
              <p className="text-xs text-ink-dim mb-0.5">{player.email}</p>
            )}
            <p className="text-xs text-ink-dim">
              ID {uid}{player.vip_agent ? ` · ${player.vip_agent}` : ''}
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5">
              {player.dt_registration && (
                <p className="text-xs text-ink-dim">
                  <span className="text-ink-mute">Registered:</span>{' '}
                  <span className="font-bold text-ink">{fmtDate(player.dt_registration)}</span>
                  <span className="text-ink-mute ml-1">({fmtYearsAgo(player.dt_registration)})</span>
                </p>
              )}
              {player.date_of_birth && (
                <p className="text-xs text-ink-dim">
                  <span className="text-ink-mute">Born:</span>{' '}
                  <span className="font-bold text-ink">{fmtDate(player.date_of_birth)}</span>
                  <span className="text-ink-mute ml-1">({fmtYearsAgo(player.date_of_birth)})</span>
                </p>
              )}
            </div>
          </div>
          {contactAction}
        </div>
        {player.wl6 && (
          <div className="flex items-center gap-2 pt-3 border-t border-white/10">
            <span className="text-[10px] text-ink-dim">6-week hold:</span>
            <WLDots wl6={player.wl6} />
          </div>
        )}
        {identityPanel}
      </div>

      {contactPanel}

      {/* Insight cards — always rendered so state changes are always visible; hidden in
          Custom range mode since AI Insights always analyses a fixed recent window. */}
      {!hidden.insights && metricsWindow.mode === 'standard' && (
      <div className="rounded-lg border border-white/5 bg-surface-1 p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] text-ink-dim uppercase tracking-widest">AI Insights</p>
          <div className="flex items-center gap-2">
            {insightStatus && <span className="text-[11px] text-ink-dim animate-pulse">{insightStatus}</span>}
            <button
              type="button"
              onClick={generateInsights}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold"
              style={{
                background: insightStreaming ? 'rgba(226,19,81,0.12)' : 'rgba(212,168,67,0.15)',
                color:      insightStreaming ? '#e05555' : '#d4a843',
                border:     `1px solid ${insightStreaming ? '#e05555' : '#d4a843'}44`,
              }}
            >
              <Sparkles className={`h-3.5 w-3.5 ${insightStreaming ? 'animate-spin' : ''}`} />
              {insightStreaming ? 'Stop' : insightCards.length > 0 ? 'Regenerate' : 'Generate'}
            </button>
          </div>
        </div>
        {insightError && (
          <div className="rounded border border-[#e05555]/30 bg-[#e05555]/10 px-3 py-2 mb-3 max-h-64 overflow-y-auto">
            <pre className="text-xs text-[#e05555] whitespace-pre-wrap font-mono">{insightError}</pre>
          </div>
        )}
        {!insightError && insightCards.length === 0 && !insightStreaming && (
          <p className="text-xs text-ink-dim">Click &quot;Generate insights&quot; to analyse the last 30 days.</p>
        )}
        {insightStreaming && insightCards.length === 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {[0,1,2].map(i => (
              <div key={i} className="rounded-lg border border-white/10 h-36 animate-pulse" style={{ borderLeftWidth: 3, borderLeftColor: '#2a3042', animationDelay: `${i * 0.15}s` }} />
            ))}
          </div>
        )}
        {insightCards.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {insightCards.map((card, i) => <InsightCard key={i} card={card} />)}
          </div>
        )}
      </div>
      )}

      {/* Metrics window control bar */}
      <MetricsWindowBar metricsWindow={metricsWindow} />

      {/* Stats organized by category */}
      <div className="flex flex-col gap-4">

        {metricsWindow.mode === 'standard' ? (
          <>
            <StatSection label="Performance" pill={<SectionPill mode="standard" />}>
              <StatCard
                label="Lifetime NGR"
                value={fmtEur(player.life_ngr)}
                color={player.life_ngr >= 0 ? METRIC_COLORS.gold : METRIC_COLORS.negative}
              />
              <DualStatCard
                label="NGR"
                value7={fmtEur(player.ngr_7d)}
                color7={player.ngr_7d >= 0 ? METRIC_COLORS.positive : METRIC_COLORS.negative}
                trend7={{ ...dailyTrend(player.ngr_7d, player.ngr_30d), fmt: fmtEur }}
                value30={fmtEur(player.ngr_30d)}
                color30={player.ngr_30d >= 0 ? METRIC_COLORS.positive : METRIC_COLORS.negative}
              />
              <DualStatCard
                label="GGR"
                value7={fmtEur(player.ggr_7d)}
                color7={player.ggr_7d >= 0 ? METRIC_COLORS.positive : METRIC_COLORS.negative}
                trend7={{ ...dailyTrend(player.ggr_7d, player.ggr_30d), fmt: fmtEur }}
                value30={fmtEur(player.ggr_30d)}
                color30={player.ggr_30d >= 0 ? METRIC_COLORS.positive : METRIC_COLORS.negative}
              />
              <DualStatCard
                label="Turnover"
                value7={fmtEur(player.turnover_7d)}
                trend7={{ ...dailyTrend(player.turnover_7d, player.turnover_30d), fmt: fmtEur }}
                value30={fmtEur(player.turnover_30d)}
              />
            </StatSection>

            <StatSection label="Money" pill={<SectionPill mode="standard" />}>
              <DualStatCard
                label="Deposits"
                value7={fmtEur(player.dep_7d)}
                trend7={{ ...dailyTrend(player.dep_7d, player.dep_30d), fmt: fmtEur }}
                value30={fmtEur(player.dep_30d)}
              />
              <DualStatCard
                label="Cashouts"
                value7={fmtEur(player.cash_7d)}
                trend7={{ ...dailyTrend(player.cash_7d, player.cash_30d), fmt: fmtEur }}
                value30={fmtEur(player.cash_30d)}
              />
              <DualStatCard
                label="Bonus"
                value7={fmtEur(player.bonus_7d)}
                color7={player.bonus_7d > 0 ? METRIC_COLORS.warning : undefined}
                trend7={{ ...dailyTrend(player.bonus_7d, player.bonus_30d), fmt: fmtEur }}
                value30={fmtEur(player.bonus_30d)}
                color30={player.bonus_30d > 0 ? METRIC_COLORS.warning : undefined}
              />
              <StatCard
                label="Playtime 7d"
                value={player.playtime || '—'}
                trend={{ ...dailyTrend(player.playtime_7d_sec, player.playtime_30d_sec), fmt: (s) => fmtMin(s / 60) }}
              />
            </StatSection>

            <StatSection label="Ratios" pill={<SectionPill mode="standard" />}>
              <DualStatCard
                label="Avg Bet"
                value7={player.avgBet7d > 0 ? `${player.avgBet7d.toFixed(1)}€` : '—'}
                value30={player.avgBet30d > 0 ? `${player.avgBet30d.toFixed(1)}€` : '—'}
              />
              <DualStatCard
                label="House Edge"
                value7={fmtPct(player.houseEdgePct7d)}
                color7={player.houseEdgePct7d != null && player.houseEdgePct7d > 0 ? METRIC_COLORS.positive : undefined}
                value30={fmtPct(player.houseEdgePct30d)}
                color30={player.houseEdgePct30d != null && player.houseEdgePct30d > 0 ? METRIC_COLORS.positive : undefined}
              />
              <DualStatCard
                label="NGR/Dep"
                value7={fmtPct(player.ngrDepRatio7d)}
                color7={player.ngrDepRatio7d != null ? (player.ngrDepRatio7d > 0 ? METRIC_COLORS.positive : METRIC_COLORS.negative) : undefined}
                value30={fmtPct(player.ngrDepRatio30d)}
                color30={player.ngrDepRatio30d != null ? (player.ngrDepRatio30d > 0 ? METRIC_COLORS.positive : METRIC_COLORS.negative) : undefined}
              />
              <DualStatCard
                label="Bonus/GGR"
                value7={fmtPct(player.bonusGgrRatio7d)}
                color7={player.bonusGgrRatio7d != null && player.bonusGgrRatio7d > 100 ? METRIC_COLORS.negative : undefined}
                value30={fmtPct(player.bonusGgrRatio30d)}
                color30={player.bonusGgrRatio30d != null && player.bonusGgrRatio30d > 100 ? METRIC_COLORS.negative : undefined}
              />
            </StatSection>
          </>
        ) : (
          <RangeStatSections metricsWindow={metricsWindow} />
        )}

        {!hidden.comps && metricsWindow.mode === 'standard' && <CompSignalPanel player={player} />}

        {(player.aff_campaign_id != null || player.aff_affiliate_id != null || player.aff_name) && (() => {
          const strategy   = (player.aff_strategy || '').toLowerCase();
          const titleLower = (player.aff_title   || '').toLowerCase();
          const fee30d     = parseFloat(player.aff_fee_30d)     || 0;
          const ngr30d     = parseFloat(player.ngr1_30d)        || 0;
          const feeLife    = parseFloat(player.aff_fee_lifetime) || 0;
          const ngrLife    = parseFloat(player.ngr1_lifetime)    || 0;
          const pctLife    = ngrLife !== 0 ? (feeLife / ngrLife * 100).toFixed(1) : '0.0';
          const isAffZero  = feeLife === 0 || titleLower.includes('zero') || titleLower.includes('empty');
          const affBadge = strategy.includes('cpa')
            ? { label: 'Aff CPA',  variant: 'warning'  }
            : isAffZero
              ? { label: 'Aff Zero', variant: 'positive' }
              : { label: 'Aff Rev',  variant: 'warning'  };

          // Custom range mode swaps the fixed 30d fee/NGR field for the chosen period —
          // Lifetime stays put, matching "Lifetime figures unaffected" elsewhere on the bar.
          const isCustom      = metricsWindow.mode === 'custom';
          const periodLoading = isCustom && metricsWindow.loading;
          const periodDays    = isCustom ? metricsWindow.resolved?.days : 30;
          const periodFee     = isCustom ? (metricsWindow.data?.current?.aff_fee ?? 0) : fee30d;
          const periodNgr     = isCustom ? (metricsWindow.data?.current?.ngr1 ?? 0) : ngr30d;
          const periodPct     = periodNgr !== 0 ? (periodFee / periodNgr * 100).toFixed(1) : '0.0';
          const periodLabel   = `Aff. Fee / NGR ${periodDays}d`;

          return (
          <div className="rounded-lg border border-white/10 bg-surface-1 px-4 pt-3 pb-4">
            <div className="flex items-center gap-2 mb-3">
              <p className="text-[9px] text-ink-dim uppercase tracking-widest">Affiliate</p>
              <BadgeChip label={affBadge.label} variant={affBadge.variant} />
              {isCustom && <SectionPill mode="custom" resolved={metricsWindow.resolved} />}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2">
              <div>
                <p className="text-[9px] text-ink-dim uppercase tracking-wide mb-0.5">Affiliate</p>
                <p className="text-xs font-semibold text-ink">{player.aff_name}</p>
              </div>
              {player.aff_title && (
                <div>
                  <p className="text-[9px] text-ink-dim uppercase tracking-wide mb-0.5">Title</p>
                  <p className="text-xs font-semibold text-ink">{player.aff_title}</p>
                </div>
              )}
              {player.aff_strategy && (
                <div>
                  <p className="text-[9px] text-ink-dim uppercase tracking-wide mb-0.5">Strategy</p>
                  <p className="text-xs font-semibold text-ink">{player.aff_strategy}</p>
                </div>
              )}
              {player.aff_admin_fee_percent != null && (
                <div>
                  <p className="text-[9px] text-ink-dim uppercase tracking-wide mb-0.5">Admin Fee</p>
                  <p className="text-xs font-semibold text-ink">{player.aff_admin_fee_percent}%</p>
                </div>
              )}
              {player.aff_fee_30d != null && (
                <div>
                  <p className="text-[9px] text-ink-dim uppercase tracking-wide mb-0.5">{periodLabel}</p>
                  {periodLoading ? (
                    <div className="h-4 w-24 rounded bg-white/10 animate-pulse" />
                  ) : (
                    <p className="text-xs font-semibold text-ink">
                      {fmtEur(periodFee)}
                      <span className="text-ink-dim font-normal"> / {fmtEur(periodNgr)}</span>
                      <span className="text-ink-dim font-normal"> · {periodPct}%</span>
                    </p>
                  )}
                </div>
              )}
              {player.aff_fee_lifetime != null && (
                <div>
                  <p className="text-[9px] text-ink-dim uppercase tracking-wide mb-0.5">Aff. Fee / NGR Lifetime</p>
                  <p className="text-xs font-semibold text-ink">
                    {fmtEur(feeLife)}
                    <span className="text-ink-dim font-normal"> / {fmtEur(ngrLife)}</span>
                    <span className="text-ink-dim font-normal"> · {pctLife}%</span>
                  </p>
                </div>
              )}
              {player.aff_affiliate_id != null && (
                <div>
                  <p className="text-[9px] text-ink-dim uppercase tracking-wide mb-0.5">Partner</p>
                  <a
                    href={`https://starzpartners.com/casino/partners/${player.aff_affiliate_id}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-xs font-semibold text-[#4fc3f7] hover:underline flex items-center gap-1"
                  >
                    #{player.aff_affiliate_id}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
              {player.aff_campaign_id != null && (
                <div>
                  <p className="text-[9px] text-ink-dim uppercase tracking-wide mb-0.5">Campaign</p>
                  <a
                    href={`https://starzpartners.com/casino/campaigns/${player.aff_campaign_id}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-xs font-semibold text-[#4fc3f7] hover:underline flex items-center gap-1"
                  >
                    #{player.aff_campaign_id}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
              {player.aff_commission_id != null && (
                <div>
                  <p className="text-[9px] text-ink-dim uppercase tracking-wide mb-0.5">Commission</p>
                  <a
                    href={`https://starzpartners.com/casino/commissions/${player.aff_commission_id}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-xs font-semibold text-[#4fc3f7] hover:underline flex items-center gap-1"
                  >
                    #{player.aff_commission_id}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
            </div>
          </div>
          );
        })()}

        {player.payMethods && player.payMethods.length > 0 && (
          <div>
            <p className="text-[9px] text-ink-dim uppercase tracking-widest mb-2">Payment Methods (7d)</p>
            <div className="flex flex-wrap gap-1.5">
              {player.payMethods.map((m, i) => (
                <span key={i} className="text-[11px] text-ink border border-white/10 rounded px-2 py-0.5 bg-surface-1">
                  {m}
                </span>
              ))}
            </div>
          </div>
        )}

      </div>

      {metricsWindow.mode === 'standard' && (
        <PlayerGamesSection games={games} days={gamesDays} setDays={setGamesDays} loading={gamesLoading} activeBrand={activeBrand} />
      )}
      <PeakTimeCard cells={heatmapCells} />
      {!hidden.poba && <PredictionsSection player={player} history={pobaHistory} metricsWindow={metricsWindow} />}
    </div>
  );
}

// ─── Tab content: Trajectory ──────────────────────────────────────────────────

function TrajectoryTabContent({ data, loading }) {
  const [zoomRange, setZoomRange] = useState([0, 179]);

  // Reset zoom whenever a new player's data arrives.
  useEffect(() => {
    setZoomRange([0, 179]);
  }, [data]);

  const handleSetZoom = useCallback((v) => setZoomRange(v), []);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[280px] rounded-lg border border-white/10 bg-surface-1 p-6">
        <Spinner size="sm" className="text-ink-dim" />
      </div>
    );
  }
  if (!data) return null;
  if (!data.found) {
    return (
      <div className="flex justify-center items-center min-h-[280px] rounded-lg border border-white/10 bg-surface-1 p-6">
        <p className="text-[13px] text-ink-dim text-center">
          No daily activity recorded for this player in the 180-day window.
        </p>
      </div>
    );
  }

  const t = data.trajectory;
  const daily = data.daily || [];
  const maxIdx = Math.max(0, daily.length - 1);
  const zStart = Math.min(zoomRange[0], maxIdx);
  const zEnd = Math.min(zoomRange[1], maxIdx);

  return (
    <div className="flex flex-col gap-4">
      {/* Sticky zoom bar — stays visible while scrolling through bonus profile */}
      {/* top: 3.5rem (56 px) = TopBar h-14 height */}
      <div
        className="sticky z-20 rounded border border-[#2a3042] bg-[#13161b] px-4 py-3"
        style={{ top: '3.5rem' }}
      >
        <ZoomBar zoomRange={zoomRange} setZoomRange={handleSetZoom} daily={daily} />
      </div>

      <TrajectoryChart
        daily={daily}
        bonusTypes={data.bonus_types}
        zoomRange={zoomRange}
        setZoomRange={handleSetZoom}
      />
      <TrajectorySummaryCard trajectory={t} />
      <BonusProfile
        trajectory={t}
        bonusTypes={data.bonus_types}
        daily={daily}
        zStart={zStart}
        zEnd={zEnd}
      />
    </div>
  );
}

// ─── Tab content: Heatmap ─────────────────────────────────────────────────────

function HeatmapTabContent({ data, loading }) {
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[280px] rounded-lg border border-white/10 bg-surface-1 p-6">
        <Spinner size="sm" className="text-ink-dim" />
      </div>
    );
  }
  if (!data) return null;
  if (!data.found) {
    return (
      <div className="flex justify-center items-center min-h-[280px] rounded-lg border border-white/10 bg-surface-1 p-6">
        <p className="text-[13px] text-ink-dim text-center">
          No heatmap data for this player in the 6-month window.
        </p>
      </div>
    );
  }
  const s = data.summary;
  return (
    <div className="flex flex-col gap-4">
      <HeatmapGrid cells={data.cells} sparse={data.sparse} />
      {s && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatTile label="Sessions" value={s.session_count?.toLocaleString() ?? '—'} />
          <StatTile
            label="Avg session"
            value={fmtMin(s.avg_session_min)}
            sub={`median ${fmtMin(s.median_session_min)}`}
          />
          <StatTile label="Longest session" value={fmtMin(s.max_session_min)} />
          <StatTile
            label="Bets / session"
            value={s.avg_bets_per_session != null ? s.avg_bets_per_session.toFixed(1) : '—'}
          />
          <StatTile label="Active days" value={s.active_days?.toLocaleString() ?? '—'} />
          <StatTile
            label="Hour entropy"
            value={
              <span style={{ color: entropyColor(s.hour_entropy) }}>
                {s.hour_entropy != null ? s.hour_entropy.toFixed(2) : '—'}
              </span>
            }
            sub={data.entropy_label || undefined}
          />
        </div>
      )}
    </div>
  );
}

// ─── Tab content: Churn ───────────────────────────────────────────────────────

function PobaTabContent({ data, loading }) {
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[280px] rounded-lg border border-white/10 bg-surface-1 p-6">
        <Spinner size="sm" className="text-ink-dim" />
      </div>
    );
  }
  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[280px] rounded-lg border border-white/10 bg-surface-1 p-6 gap-3">
        <Activity className="w-8 h-8 text-ink-dim opacity-40" />
        <p className="text-sm font-bold text-ink-dim">Churn & LTV data not available</p>
        <p className="text-[11px] text-ink-mute text-center max-w-xs">
          Prediction models require sufficient betting history. This player does not have enough data to generate PoBA or CLTV scores.
        </p>
      </div>
    );
  }
  return <PobaChart rows={data} />;
}

// ─── Tab content: Insights ───────────────────────────────────────────────────

const URGENCY_STYLES = {
  high:   { border: '#E21351', badge: 'rgba(226,19,81,0.15)',  text: '#E21351',  label: 'HIGH'   },
  medium: { border: '#d4a843', badge: 'rgba(212,168,67,0.15)', text: '#d4a843',  label: 'MEDIUM' },
  low:    { border: '#00BD71', badge: 'rgba(0,189,113,0.15)',  text: '#00BD71',  label: 'LOW'    },
};

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button onClick={copy} className="p-1 rounded hover:bg-white/10 transition-colors" title="Copy">
      {copied
        ? <Check className="h-3 w-3" style={{ color: '#00BD71' }} />
        : <Copy className="h-3 w-3 text-ink-dim" />}
    </button>
  );
}

function InsightCard({ card }) {
  const [expanded, setExpanded] = useState(false);
  const style = URGENCY_STYLES[card.urgency] || URGENCY_STYLES.medium;

  const evidenceLines = (card.evidence || '')
    .split('\n')
    .map(l => l.trim())
    .filter(l => l.length > 0);

  return (
    <div
      className="rounded-lg border bg-surface-1 flex flex-col overflow-hidden"
      style={{ borderColor: style.border + '44', borderLeftColor: style.border, borderLeftWidth: 3 }}
    >
      {/* Header */}
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-start justify-between gap-2 mb-2">
          <span
            className="text-[9px] font-bold tracking-widest px-1.5 py-0.5 rounded"
            style={{ background: style.badge, color: style.text }}
          >
            {style.label}
          </span>
        </div>
        <p className="text-xs font-bold text-ink uppercase tracking-wide mb-2">{card.trigger}</p>
        <p className="text-[11px] text-ink leading-relaxed">{card.insight}</p>
      </div>

      {/* Evidence */}
      {evidenceLines.length > 0 && (
        <div className="px-4 pb-3 border-t border-white/5 pt-3">
          <p className="text-[9px] text-ink-dim uppercase tracking-widest mb-1.5">Evidence</p>
          <ul className="space-y-1">
            {evidenceLines.map((line, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="text-[10px] text-ink-dim mt-px">•</span>
                <span className="text-[11px] text-ink-dim leading-snug">{line.replace(/^[•-]\s*/, '')}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Action details — toggle */}
      <button
        onClick={() => setExpanded(e => !e)}
        className="px-4 py-2 text-[10px] text-ink-dim hover:text-ink flex items-center gap-1 border-t border-white/5 transition-colors"
      >
        {expanded ? '▲' : '▼'} {expanded ? 'Hide action plan' : 'Show action plan'}
      </button>
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-white/5 pt-3">
          {card.action && (
            <div>
              <p className="text-[9px] text-ink-dim uppercase tracking-widest mb-1">Action</p>
              <p className="text-[11px] text-ink leading-relaxed">{card.action}</p>
            </div>
          )}
          {card.say && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-[9px] text-ink-dim uppercase tracking-widest">Say</p>
                <CopyButton text={card.say} />
              </div>
              <div className="rounded bg-[#0d0f12] border border-white/8 px-3 py-2">
                <p className="text-[11px] text-ink leading-relaxed whitespace-pre-wrap">{card.say}</p>
              </div>
            </div>
          )}
          {card.avoid && (
            <div>
              <p className="text-[9px] text-ink-dim uppercase tracking-widest mb-1">Avoid</p>
              <p className="text-[11px] text-ink-dim leading-relaxed">{card.avoid}</p>
            </div>
          )}
          {card.followup && (
            <div>
              <p className="text-[9px] text-ink-dim uppercase tracking-widest mb-1">Follow-up</p>
              <p className="text-[11px] text-ink leading-relaxed">{card.followup}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function parseInsightCards(text) {
  const result = [];
  const regex = /CARD_START([\s\S]*?)CARD_END/g;
  let m;
  while ((m = regex.exec(text)) !== null) {
    const block = m[1];
    const lines = block.split('\n');
    const sections = {};
    let currentKey = null;
    let currentLines = [];
    for (const line of lines) {
      // Match field names case-insensitively; tolerate markdown bold (**KEY:**)
      const keyMatch = line.match(/^\*{0,2}([A-Za-z_]+)\*{0,2}:\s*(.*)/);
      if (keyMatch) {
        if (currentKey) sections[currentKey] = currentLines.join('\n').trim();
        currentKey = keyMatch[1].toUpperCase();
        currentLines = keyMatch[2] ? [keyMatch[2]] : [];
      } else if (currentKey) {
        currentLines.push(line);
      }
    }
    if (currentKey) sections[currentKey] = currentLines.join('\n').trim();
    result.push({
      urgency:  (sections.URGENCY  || 'medium').toLowerCase(),
      trigger:  sections.TRIGGER  || '',
      insight:  sections.INSIGHT  || '',
      evidence: sections.EVIDENCE || '',
      action:   sections.ACTION   || '',
      say:      sections.SAY      || '',
      avoid:    sections.AVOID    || '',
      followup: sections.FOLLOWUP || '',
    });
  }
  return result;
}

function inferTrajectory(data) {
  if (!data) return null;
  if (!data.found) return 'inactive';
  const slope = data.trajectory?.stake_slope ?? 0;
  if (slope > 0.3) return 'up';
  if (slope < -0.3) return 'down';
  return 'flat';
}

// fromRoutes maps the profile's ?from= to the page the back link returns to; the dashboard is the
// active VIP source's own.
function fromRoutes(paths) {
  return {
    dashboard:  { path: paths.dashboard,      label: 'Dashboard' },
    trajectory: { path: '/player-trajectory', label: 'Trajectory book' },
    heatmap:    { path: '/heatmap',           label: 'Heatmap book' },
    tracker:    { path: '/contact-tracker',   label: 'Contact Tracker' },
  };
}

export default function PlayerProfilePage() {
  const { userId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeBrand } = useBrand();
  const { api, hidden, paths, canIssueBonus } = useVipSource();
  const [bonusOpen, setBonusOpen] = useState(false);

  const uid = parseInt(userId, 10);
  const hasChurn = !hidden.poba;
  const rawTab = searchParams.get('tab') || 'overview';

  // Dashboard passes enriched player via navigate state; otherwise we fetch it.
  // Always run enrichPlayerStats so ratio fields (avgBet7d, houseEdgePct7d, etc.) are present
  // regardless of which path sourced the player.
  const playerFromState = location.state?.player ? enrichPlayerStats(location.state.player) : null;
  const [fetchedPlayer, setFetchedPlayer] = useState(null);
  const [playerStatsLoaded, setPlayerStatsLoaded] = useState(false);
  const [playerNoData, setPlayerNoData] = useState(false);
  const player = fetchedPlayer || playerFromState;

  const hasHeatmap = player?.is_vip === true;
  const activeTab = (!hasChurn && rawTab === 'churn') || (!hasHeatmap && rawTab === 'heatmap')
    ? 'overview'
    : rawTab;

  const [trajectoryData, setTrajectoryData] = useState(null);
  const [trajectoryLoading, setTrajectoryLoading] = useState(false);
  const [trajectoryLoaded, setTrajectoryLoaded] = useState(false);

  const [heatmapData, setHeatmapData] = useState(null);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [heatmapLoaded, setHeatmapLoaded] = useState(false);

  const [pobaData, setPobaData] = useState(null);
  const [pobaLoading, setPobaLoading] = useState(false);
  const [pobaLoaded, setPobaLoaded] = useState(false);

  const [error, setError] = useState('');

  // Always fetch full player stats — navigate state from dashboard is missing
  // affiliate fields and other per-player data not in the dashboard query.
  useEffect(() => {
    if (!Number.isInteger(uid) || uid <= 0) return;
    if (playerStatsLoaded) return;
    const controller = new AbortController();
    api.getPlayerStats(uid, controller.signal)
      .then((res) => {
        const raw = res.data?.data;
        if (!raw) return;
        setFetchedPlayer(enrichPlayerStats(raw));
        setPlayerStatsLoaded(true);
      })
      .catch((err) => {
        if (err?.code?.includes('ERR_CANCELED')) return;
        if (err?.response?.status === 404 && !playerFromState) setPlayerNoData(true);
        setPlayerStatsLoaded(true);
      });
    return () => controller.abort();
  }, [uid, playerStatsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch trajectory on mount always — needed for avatar color and header identity
  // even when the user lands on a different tab or arrives via direct URL.
  useEffect(() => {
    if (!Number.isInteger(uid) || uid <= 0) {
      setError('Invalid player ID.');
      return;
    }
    if (trajectoryLoaded) return;
    const controller = new AbortController();
    setTrajectoryLoading(true);
    api.getTrajectoryPlayer(uid, controller.signal)
      .then((res) => {
        setTrajectoryData(res.data);
        setTrajectoryLoaded(true);
      })
      .catch((err) => {
        if (err?.code?.includes('ERR_CANCELED')) return;
        if (activeTab === 'trajectory') setError(err?.response?.data?.error || 'Failed to load trajectory data.');
      })
      .finally(() => setTrajectoryLoading(false));
    return () => controller.abort();
  }, [uid, trajectoryLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!Number.isInteger(uid) || uid <= 0) return;
    if (!hasHeatmap) return;
    if ((activeTab !== 'heatmap' && activeTab !== 'overview') || heatmapLoaded) return;
    const controller = new AbortController();
    setHeatmapLoading(true);
    setError('');
    api.getHeatmapPlayer(uid, controller.signal)
      .then((res) => {
        setHeatmapData(res.data);
        setHeatmapLoaded(true);
      })
      .catch((err) => { if (!err?.code?.includes('ERR_CANCELED')) setError(err?.response?.data?.error || 'Failed to load heatmap data.'); })
      .finally(() => setHeatmapLoading(false));
    return () => controller.abort();
  }, [activeTab, uid, heatmapLoaded, hasHeatmap, api]);

  useEffect(() => {
    if (!Number.isInteger(uid) || uid <= 0) return;
    if (pobaLoaded || !hasChurn) return;
    const controller = new AbortController();
    setPobaLoading(true);
    api.getPobaHistory(uid, controller.signal)
      .then((res) => {
        setPobaData(res.data.rows || []);
        setPobaLoaded(true);
      })
      .catch((err) => {
        if (err?.code?.includes('ERR_CANCELED')) return;
        setPobaData([]);
        setPobaLoaded(true);
      })
      .finally(() => setPobaLoading(false));
    return () => controller.abort();
  }, [uid, pobaLoaded, hasChurn]); // eslint-disable-line react-hooks/exhaustive-deps

  const [gamesData, setGamesData] = useState(null);
  const [gamesDays, setGamesDays] = useState(7);
  const [gamesLoading, setGamesLoading] = useState(false);

  useEffect(() => {
    if (!Number.isInteger(uid) || uid <= 0) return;
    const controller = new AbortController();
    setGamesLoading(true);
    api.getPlayerGames(uid, gamesDays, controller.signal)
      .then((res) => setGamesData(res.data?.games || []))
      .catch((err) => { if (!err?.code?.includes('ERR_CANCELED')) setGamesData([]); })
      .finally(() => setGamesLoading(false));
    return () => controller.abort();
  }, [uid, gamesDays]); // eslint-disable-line react-hooks/exhaustive-deps

  const from = searchParams.get('from');
  const rawFromView = searchParams.get('view');
  const fromView = ['cards', 'table'].includes(rawFromView) ? rawFromView : null;

  // Metrics window (Performance/Money/Ratios date-range mode) — persisted alongside
  // tab/from in the URL so it survives reload and is shareable. Custom mode is either
  // a named preset (?range=14d) or an explicit pair (?date_from=&date_to=).
  const mwRangeParam = searchParams.get('range');
  const mwDateFromParam = searchParams.get('date_from');
  const mwDateToParam = searchParams.get('date_to');
  const mwCompareParam = searchParams.get('compare');

  const mwMode = (mwRangeParam || (mwDateFromParam && mwDateToParam)) ? 'custom' : 'standard';
  const mwPreset = mwMode === 'custom' ? (mwRangeParam || 'custom') : null;
  const mwCompare = mwCompareParam !== '0';
  const mwResolved = useMemo(
    () => (mwMode === 'custom' ? resolveMetricsRange({ preset: mwPreset, dateFrom: mwDateFromParam, dateTo: mwDateToParam }) : null),
    [mwMode, mwPreset, mwDateFromParam, mwDateToParam]
  );

  // Single place that knows every persisted query key — always rewrites the full set
  // so switching tabs never clobbers the metrics window (and vice versa).
  const updateParams = (patch) => {
    const next = {};
    const nextTab = 'tab' in patch ? patch.tab : activeTab;
    if (nextTab) next.tab = nextTab;
    const nextFrom = 'from' in patch ? patch.from : from;
    if (nextFrom) next.from = nextFrom;
    const nextView = 'view' in patch ? patch.view : fromView;
    if (nextView) next.view = nextView;
    for (const key of ['range', 'date_from', 'date_to', 'compare']) {
      const v = key in patch ? patch[key] : searchParams.get(key);
      if (v != null && v !== '') next[key] = v;
    }
    setSearchParams(next, { replace: true, state: location.state });
  };

  const setTab = (tab) => updateParams({ tab });

  const setMwMode = (mode) => {
    if (mode === 'standard') updateParams({ range: null, date_from: null, date_to: null });
    else updateParams({ range: mwPreset || DEFAULT_CUSTOM_PRESET, date_from: null, date_to: null });
  };
  const setMwPreset = (presetKey) => updateParams({ range: presetKey, date_from: null, date_to: null });
  const setMwCustomRange = (dateFrom, dateTo) => updateParams({ range: null, date_from: dateFrom, date_to: dateTo });
  const setMwCompare = (on) => updateParams({ compare: on ? null : '0' });

  const [mwData, setMwData] = useState(null);
  const [mwLoading, setMwLoading] = useState(false);

  useEffect(() => {
    if (mwMode !== 'custom' || !mwResolved || !Number.isInteger(uid) || uid <= 0) {
      setMwData(null);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    setMwLoading(true);
    api.getPlayerStatsRange(uid, { from: mwResolved.fromStr, to: mwResolved.toStr, compare: mwCompare }, controller.signal)
      .then((res) => { if (!cancelled) setMwData(res.data); })
      .catch((err) => { if (!cancelled && !err?.code?.includes('ERR_CANCELED')) setMwData(null); })
      .finally(() => { if (!cancelled) setMwLoading(false); });
    return () => { cancelled = true; controller.abort(); };
  }, [uid, mwMode, mwResolved?.fromStr, mwResolved?.toStr, mwCompare]); // eslint-disable-line react-hooks/exhaustive-deps

  const metricsWindow = {
    mode: mwMode,
    preset: mwPreset,
    dateFrom: mwDateFromParam,
    dateTo: mwDateToParam,
    compare: mwCompare,
    resolved: mwResolved,
    data: mwData,
    loading: mwLoading,
    setMode: setMwMode,
    setPreset: setMwPreset,
    setCustomRange: setMwCustomRange,
    setCompare: setMwCompare,
  };

  const backRoutes = fromRoutes(paths);
  const backDest = backRoutes[from] || backRoutes.dashboard;
  const backPath = fromView ? `${backDest.path}?view=${fromView}` : backDest.path;

  // Identity: prefer dashboard state, fall back to API data as it loads
  const displayName =
    player?.username || player?.email ||
    trajectoryData?.trajectory?.email ||
    heatmapData?.summary?.email ||
    `Player ${uid}`;

  const displayAgent =
    player?.vip_agent ||
    trajectoryData?.trajectory?.vip_agent ||
    heatmapData?.summary?.vip_agent ||
    null;

  const initials = player?.initials || playerInitials(displayName);
  // player state (from dashboard navigation) is authoritative; fall back to API inference
  const resolvedTrajectory = player?.trajectory || inferTrajectory(trajectoryData);
  const contactLog = usePlayerContactLog({ userId: uid, playerName: displayName, ngr30d: player?.ngr_30d, enabled: !hidden.contacts });
  // The private dashboard issues bonuses from the profile: the button takes the contact log's place
  // and opens the form inside the identity card.
  const bonusButton = canIssueBonus ? (
    <Button
      type="button" variant={bonusOpen ? 'outline' : 'default'} className="ml-auto h-11 shrink-0 gap-2 px-5"
      aria-expanded={bonusOpen} aria-controls="issue-bonus" onClick={() => setBonusOpen((o) => !o)}
    >
      {bonusOpen ? <ChevronUp className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
      {bonusOpen ? 'Hide bonus form' : 'Issue bonus'}
    </Button>
  ) : null;
  const bonusForm = canIssueBonus && bonusOpen
    ? <IssueBonusForm userId={uid} onClose={() => setBonusOpen(false)} bonusesPath={paths.bonuses} />
    : null;

  if (!Number.isInteger(uid) || uid <= 0) {
    return (
      <div className="max-w-[1320px] mx-auto">
        <Alert variant="error" className="text-xs">
          <AlertDescription>Invalid player ID in URL.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="max-w-[1320px] mx-auto">
      {/* Back */}
      <button
        type="button"
        onClick={() => navigate(backPath)}
        className="inline-flex items-center gap-1 text-xs text-ink-dim cursor-pointer mb-4 select-none hover:text-cta transition-colors"
      >
        ← {backDest.label}
      </button>

      {/* Slim header */}
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-bold text-ink">{displayName}</h1>
          <a
            href={`${BACKOFFICE_PLAYER_URL[activeBrand] || BACKOFFICE_PLAYER_URL.bitstarz}/${uid}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-ink-dim hover:text-ink transition-colors"
            title="Open in backoffice"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
        <p className="text-[11px] text-ink-dim mt-0.5">
          User ID: {uid}{displayAgent ? ` · Manager: ${displayAgent}` : ''}
        </p>
      </div>

      {/* Error banner */}
      {error && (
        <Alert variant="error" className="mb-4 text-xs">
          <AlertDescription className="flex items-center justify-between gap-2">
            <span>{error}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setError('')}
              className="shrink-0 text-negative hover:text-negative h-auto px-1 py-0"
              aria-label="Dismiss error"
            >
              ✕
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Tabs */}
      <div className="flex border-b border-white/10 mb-6">
        <TabButton
          active={activeTab === 'overview'}
          onClick={() => setTab('overview')}
          icon={User}
          label="Overview"
        />
        <TabButton
          active={activeTab === 'trajectory'}
          onClick={() => setTab('trajectory')}
          icon={TrendingUp}
          label="Trajectory"
        />
        {hasHeatmap && (
          <TabButton
            active={activeTab === 'heatmap'}
            onClick={() => setTab('heatmap')}
            icon={Map}
            label="Heatmap"
          />
        )}
        {hasChurn && (
          <TabButton
            active={activeTab === 'churn'}
            onClick={() => setTab('churn')}
            icon={Activity}
            label="Churn & CLTV"
          />
        )}
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && playerNoData && (
        <div className="rounded-lg border border-white/10 bg-surface-1 p-8 text-center">
          <p className="text-sm font-bold text-ink mb-1">No activity data available</p>
          <p className="text-xs text-ink-dim">This player has not deposited in the last year and has no recent stats on record.</p>
        </div>
      )}
      {activeTab === 'overview' && !playerNoData && (
        <OverviewTabContent player={player} uid={uid} initials={initials} resolvedTrajectory={resolvedTrajectory} pobaHistory={pobaData} heatmapCells={heatmapData?.cells} games={gamesData} gamesDays={gamesDays} setGamesDays={setGamesDays} gamesLoading={gamesLoading} metricsWindow={metricsWindow}
          contactAction={contactLog.button || bonusButton} contactPanel={contactLog.panel} identityPanel={bonusForm} hidden={hidden}
        />
      )}
      {activeTab === 'trajectory' && (
        <TrajectoryTabContent data={trajectoryData} loading={trajectoryLoading} />
      )}
      {hasHeatmap && activeTab === 'heatmap' && (
        <HeatmapTabContent data={heatmapData} loading={heatmapLoading} />
      )}
      {hasChurn && activeTab === 'churn' && (
        <PobaTabContent data={pobaData} loading={pobaLoading} />
      )}
    </div>
  );
}
